import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, LOGO, NAV_LINKS, WA_URL } from "./data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`nav-shell fixed top-0 left-0 right-0 z-50 ${
          scrolled
            ? "py-3 bg-[color:var(--paper)]/95 glass border-b hairline"
            : "py-5 bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 flex items-center justify-between">
          {/* Logo + wordmark */}
          <a
            href="#top"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <span className="relative inline-block h-11 w-11 rounded-full overflow-hidden ring-1 ring-[color:var(--line)] bg-[color:var(--paper-2)]">
              <img
                src={LOGO}
                alt="Hashtag Sakleshpura Homestay logo"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </span>
            <div className="leading-none min-w-0 flex-1">
              <div
                className={`font-display whitespace-nowrap tracking-tight text-[clamp(0.78rem,3.4vw,1.4rem)] sm:text-[1.4rem] ${
                  scrolled ? "text-[color:var(--ink)]" : "text-[color:var(--paper)]"
                }`}
                style={{
                  "--sh-base": scrolled
                    ? "hsl(140, 45%, 12%)"
                    : "hsl(48, 46%, 92%)",
                }}
                data-testid="nav-wordmark"
              >
                <span className="shimmer-word">Hashtag</span>{" "}
                <span className="shimmer-word delay-1">Sakleshpura</span>{" "}
                <span className="shimmer-word delay-2">Homestay</span>
                <span className="font-display-italic text-[color:var(--clay)]">.</span>
              </div>
              <div
                className={`mt-1 font-mono-alt hidden sm:block ${
                  scrolled
                    ? "text-[color:var(--forest)]/70"
                    : "text-[color:var(--paper)]/75"
                }`}
              >
                Sakleshpura · Est. Coffee Country
              </div>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`hover-underline text-[0.92rem] font-medium ${
                  scrolled ? "text-[color:var(--ink)]" : "text-[color:var(--paper)]"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-3">
            <a
              data-testid="nav-book-whatsapp"
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[0.85rem] font-semibold transition-all ${
                scrolled
                  ? "bg-[color:var(--forest)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]"
                  : "bg-[color:var(--paper)] text-[color:var(--ink)] hover:bg-[color:var(--forest)] hover:text-[color:var(--paper)]"
              }`}
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#25D366] animate-pulse" />
              Book on WhatsApp
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              className={`lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border ${
                scrolled
                  ? "border-[color:var(--line)] text-[color:var(--ink)]"
                  : "border-[color:var(--line-inv)] text-[color:var(--paper)]"
              }`}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[color:var(--ink)] text-[color:var(--paper)] lg:hidden"
          >
            <div className="h-full flex flex-col justify-between pt-28 pb-10 px-6">
              <nav className="flex flex-col gap-4">
                {NAV_LINKS.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.7, ease: [0.2, 0.9, 0.2, 1] }}
                    className="font-display text-5xl leading-none border-b hairline-inv pb-4"
                  >
                    <span className="font-mono-alt text-[color:var(--sand)] mr-4 align-super text-xs">
                      0{i + 1}
                    </span>
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary self-start"
                onClick={() => setOpen(false)}
              >
                Book on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
